import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Company } from 'src/app/interfaces/company';
import { PuzzleService } from 'src/app/services/puzzle.service';
import { ResponseDialogComponent } from '../response-dialog/response-dialog.component';
import { LocalService } from 'src/app/services/local.service';
import { APIResponse } from 'src/app/interfaces/response';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { forkJoin, Observable, of } from 'rxjs';

@Component({
  selector: 'app-my-company-config',
  templateUrl: './my-company-config.component.html',
  styleUrls: ['./my-company-config.component.css'],
})
export class MyCompanyConfigComponent implements OnInit {
  cvPreference: boolean = false;
  cvOriginalPreference: boolean = false;
  company: Company;
  naturalDays: boolean = false;
  daysPerWeek: number = 0;
  sending: boolean = false;
  token: APIResponse;
  openedDialogResponse: boolean = false;

  constructor(
    public puzzleService: PuzzleService,
    public translate: TranslateService,
    public dialog: MatDialog,
    public localService: LocalService,
    public renderer: Renderer2,
    public dialogRef: MatDialogRef<MyCompanyConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { company: Company },
  ) {
    this.company = data.company;
    this.naturalDays = this.company.comP_USE_NATURAL_DAYS;
    this.daysPerWeek = this.company.comP_WORKING_DAYS_PER_WEEK;
    this.token = this.localService.getJsonValue('token');
    this.cvOriginalPreference = this.company.comP_CV_CLIENT;
    this.cvPreference = this.company.comP_CV_CLIENT;
  }

  ngOnInit(): void {}

  closeModal() {
    if (!this.sending) {
      this.dialogRef.close();
    }
  }

  async saveCompany() {
    let savingReal = false;
    this.sending = true;
    // Evitar que se cierre el modal mientras se procesan las peticiones
    this.dialogRef.disableClose = true;
    
    // Array para almacenar las observables de las peticiones
    const apiCalls: Observable<any>[] = [];
    
    // si naturalDays diff de company.comP_USE_NATURAL_DAYS o daysPerWeek diff de company.comP_WORKING_DAYS_PER_WEEK llamar a editOrCreateCompany
    if (
      this.naturalDays != this.company.comP_USE_NATURAL_DAYS ||
      this.daysPerWeek != this.company.comP_WORKING_DAYS_PER_WEEK
    ) {
      apiCalls.push(this.editOrCreateCompanyObservable());
      savingReal = true;
    }

    if (this.cvPreference != this.cvOriginalPreference) {
      apiCalls.push(this.saveCvPreferenceObservable());
      savingReal = true;
    }

    if (!savingReal) {
      // Si no hay cambios reales, mostrar mensaje de éxito
      setTimeout(() => {
        let translatedMessage = this.translate.instant(
          'companyUpdatedSuccessfully',
        );
        const dialog = this.openResponseDialog('Exito', translatedMessage);

        dialog.afterOpened().subscribe(() => {
          this.dialogRef.close();
          this.sending = false;
          this.dialogRef.disableClose = false;
        });
      }, 500);
      return;
    }
    
    // Esperar a que todas las peticiones terminen
    forkJoin(apiCalls).subscribe(
      (results) => {
        // Verificar si todas las peticiones fueron exitosas
        const allSuccessful = results.every(result => result.isSuccess);
        
        let translatedMessage = '';
        if (allSuccessful) {
          translatedMessage = this.translate.instant('companyUpdatedSuccessfully');
          if (!this.openedDialogResponse) {
            this.openedDialogResponse = true;
            const dialog = this.openResponseDialog('Exito', translatedMessage);
            
            dialog.afterOpened().subscribe(() => {
              this.dialogRef.close();
            });
            
            dialog.afterClosed().subscribe(() => {
              this.openedDialogResponse = false;
              window.location.reload();
            });
          }
        } else {
          translatedMessage = this.translate.instant('operationErrorRetry');
          if (!this.openedDialogResponse) {
            this.openedDialogResponse = true;
            const dialog = this.openResponseDialog('Error', translatedMessage);
            
            dialog.afterClosed().subscribe(() => {
              this.openedDialogResponse = false;
            });
          }
        }
        
        this.sending = false;
        this.dialogRef.disableClose = false;
      },
      (error) => {
        // Manejar errores
        if (!this.openedDialogResponse) {
          this.openedDialogResponse = true;
          const translatedMessage = this.translate.instant('operationErrorRetry');
          const dialog = this.openResponseDialog('Error', translatedMessage);
          
          dialog.afterClosed().subscribe(() => {
            this.openedDialogResponse = false;
          });
        }
        
        this.sending = false;
        this.dialogRef.disableClose = false;
      }
    );
  }

  // Método auxiliar que devuelve un Observable para usar con forkJoin
  editOrCreateCompanyObservable(): Observable<any> {
    return this.puzzleService.createCompany(this.company);
  }
  
  // Mantener el método original para compatibilidad con código existente
  editOrCreateCompany() {
    if (this.sending) {
      return;
    }

    this.sending = true;
    // evitar q se cierre el modal
    this.dialogRef.disableClose = true;
    return this.puzzleService.createCompany(this.company).subscribe(
      (response) => {
        let translatedMessage = '';
        if (response.isSuccess) {
          translatedMessage = this.translate.instant(
            'companyUpdatedSuccessfully',
          );

          if (this.openedDialogResponse) {
            return;
          }
          this.openedDialogResponse = true;
          const dialog = this.openResponseDialog('Exito', translatedMessage);

          dialog.afterOpened().subscribe(() => {
            this.dialogRef.close();
          });

          dialog.afterClosed().subscribe(() => {
            this.openedDialogResponse = false;
            window.location.reload();
          });
        } else {
          translatedMessage = this.translate.instant('operationErrorRetry');
          if (this.openedDialogResponse) {
            return;
          }
          this.openedDialogResponse = true;
          const dialog = this.openResponseDialog('Error', translatedMessage);

          dialog.afterClosed().subscribe(() => {
            this.openedDialogResponse = false;
          });
        }
        this.sending = false;
        this.dialogRef.disableClose = false;
      },
      (error) => {
        this.sending = false;
        this.dialogRef.disableClose = false;
      },
    );
  }

  // Método auxiliar que devuelve un Observable para usar con forkJoin
  saveCvPreferenceObservable(): Observable<any> {
    const cvData = {
      COMP_ID: this.company.comP_ID,
      COMP_CV_CLIENT: this.cvPreference,
    };
    return this.puzzleService.saveCvPreference(cvData);
  }
  
  // Mantener el método original para compatibilidad con código existente
  saveCvPreference() {
    if (this.sending) {
      return;
    }

    this.sending = true;

    const cvData = {
      COMP_ID: this.company.comP_ID,
      COMP_CV_CLIENT: this.cvPreference,
    };
    this.puzzleService.saveCvPreference(cvData).subscribe(
      (response) => {
        let translatedMessage = '';
        if (response.isSuccess) {
          translatedMessage = this.translate.instant(
            'companyUpdatedSuccessfully',
          );
          if (this.openedDialogResponse) {
            return;
          }
          this.openedDialogResponse = true;
          const dialog = this.openResponseDialog('Exito', translatedMessage);

          dialog.afterOpened().subscribe(() => {
            this.dialogRef.close();
          });

          dialog.afterClosed().subscribe(() => {
            this.openedDialogResponse = false;
            window.location.reload();
          });
        } else {
          translatedMessage = this.translate.instant('operationErrorRetry');
          if (this.openedDialogResponse) {
            return;
          }
          this.openedDialogResponse = true;
          const dialog = this.openResponseDialog('Error', translatedMessage);

          dialog.afterClosed().subscribe(() => {
            this.openedDialogResponse = false;
          });
        }
        this.sending = false;
      },
      (error) => {
        this.sending = false;
      },
    );
  }

  openResponseDialog(response: string, message?: string) {
    let data: string[] = [];
    if (response == 'Error') {
      data.push('Error');
      data.push(message);
      return this.dialog.open(ResponseDialogComponent, {
        width: '500px',
        /* height: '400px', */
        data: data,
      });
    } else if (response == 'Exito') {
      data.push('Exito');
      data.push(message);

      const dialogRef = this.dialog.open(ResponseDialogComponent, {
        width: '500px',
        /* height: '400px', */
        data: data,
      });
      this.puzzleService
        .getCompanyById(this.company.comP_ID)
        .subscribe((company) => {
          this.company = company;
        });

      return dialogRef;
    }
  }

  onFocus(event: FocusEvent) {
    const target = event.target as HTMLElement;
    this.renderer.addClass(target, 'focus-input');
  }

  onBlur(event: FocusEvent) {
    const target = event.target as HTMLElement;
    this.renderer.removeClass(target, 'focus-input');
  }

  onCvPreferenceChange(event: MatSlideToggleChange) {
    this.cvPreference = event.checked;
  }
}
